import { Sidebar } from "@heroui-pro/react/sidebar";
import { Avatar, Button, Dropdown, Label } from "@heroui/react";
import type {
  WorkspacePermission,
  WorkspaceRoleSummary,
  WorkspaceStatus,
} from "@metaadsmanager/tk-master-contracts";
import { ShieldCheck } from "@gravity-ui/icons";

import {
  adminNavItems,
  resolveWorkspaceMenuItems,
  toStaticExportHref,
  type ApplicationMode,
  type NavItem,
} from "../../config/navigation";
import { useAppI18n } from "../../i18n/context";
import { workspaceRoleLabel } from "../../lib/presentation";

export type MembershipSummary = {
  permissions: WorkspacePermission[];
  role: WorkspaceRoleSummary;
  workspace: { id: string; name: string; status: WorkspaceStatus };
};

export type DashboardSidebarProps = {
  mode: ApplicationMode;
  currentNavigationHref?: string;
  membership?: MembershipSummary;
  /**
   * `/v1/me` 是否已结算。工作区模式下未就绪时不按空权限过滤，也不按暂停态收缩主导航。
   * 管理端无成员依赖，默认 true。
   */
  membershipReady?: boolean;
  /** 刷新恢复：me 未就绪时使用上次权限快照稳定菜单。 */
  stalePermissions?: readonly WorkspacePermission[];
  /** 刷新恢复：me 未就绪时使用上次工作区状态，避免暂停主导航闪开。 */
  staleWorkspaceStatus?: WorkspaceStatus;
  staleIsWorkspaceOwner?: boolean;
  memberships?: readonly MembershipSummary[];
  onSelectWorkspace?: (workspaceId: string) => void;
};

function NavigationItems({
  currentNavigationHref,
  items,
}: {
  currentNavigationHref?: string;
  items: readonly NavItem[];
}) {
  const { t } = useAppI18n();
  return items.map(({ href, icon: Icon, labelKey, children }) => {
    const hasChildren = Boolean(children?.length);
    if (!href && !hasChildren) return null;

    const label = t(labelKey);
    const isCurrent = Boolean(href && currentNavigationHref === href);
    const exportHref = href ? toStaticExportHref(href) : undefined;
    return (
      <Sidebar.MenuItem
        href={exportHref}
        id={labelKey}
        isCurrent={isCurrent}
        key={labelKey}
        textValue={label}
        tooltip={label}
      >
        <Sidebar.MenuIcon>
          <Icon width={17} height={17} />
        </Sidebar.MenuIcon>
        <Sidebar.MenuLabel aria-current={isCurrent ? "page" : undefined}>
          {label}
          {hasChildren ? (
            <Sidebar.MenuTrigger>
              <Sidebar.MenuIndicator />
            </Sidebar.MenuTrigger>
          ) : null}
        </Sidebar.MenuLabel>
        {hasChildren ? (
          <Sidebar.Submenu>
            {children!.map((child) => {
              const ChildIcon = child.icon;
              const childLabel = t(child.labelKey);
              const childCurrent = currentNavigationHref === child.href;
              const childExportHref = child.href
                ? toStaticExportHref(child.href)
                : undefined;
              return (
                <Sidebar.MenuItem
                  href={childExportHref}
                  id={child.labelKey}
                  isCurrent={childCurrent}
                  key={child.labelKey}
                  textValue={childLabel}
                  tooltip={childLabel}
                >
                  <Sidebar.MenuIcon>
                    <ChildIcon width={17} height={17} />
                  </Sidebar.MenuIcon>
                  <Sidebar.MenuLabel
                    aria-current={childCurrent ? "page" : undefined}
                  >
                    {childLabel}
                  </Sidebar.MenuLabel>
                </Sidebar.MenuItem>
              );
            })}
          </Sidebar.Submenu>
        ) : null}
      </Sidebar.MenuItem>
    );
  });
}

/** 当前页落在子菜单时展开对应父级。 */
function expandedNavKeys(
  items: readonly NavItem[],
  currentNavigationHref?: string,
): string[] {
  if (!currentNavigationHref) return [];
  return items
    .filter((item) =>
      item.children?.some((child) => child.href === currentNavigationHref),
    )
    .map((item) => item.labelKey);
}

function WorkspaceIdentityMark({
  membership,
}: {
  membership: MembershipSummary;
}) {
  const { t } = useAppI18n();
  return (
    <>
      <Avatar size="sm">
        <Avatar.Fallback>
          {membership.workspace.name.slice(0, 2).toUpperCase()}
        </Avatar.Fallback>
      </Avatar>
      <div>
        <strong>{membership.workspace.name}</strong>
        <small>{workspaceRoleLabel(membership.role, t)}</small>
      </div>
    </>
  );
}

/** 多于一个工作空间时从侧栏切换；只有一个时只展示当前身份。 */
function WorkspaceIdentity({
  membership,
  memberships,
  onSelectWorkspace,
}: {
  membership: MembershipSummary;
  memberships: readonly MembershipSummary[];
  onSelectWorkspace?: (workspaceId: string) => void;
}) {
  const { t } = useAppI18n();
  const canSwitch = memberships.length > 1 && Boolean(onSelectWorkspace);

  if (!canSwitch || !onSelectWorkspace) {
    return (
      <div className="application-sidebar__identity">
        <WorkspaceIdentityMark membership={membership} />
      </div>
    );
  }

  return (
    <Dropdown>
      <Button
        aria-label={t("shell.switchWorkspace")}
        className="application-sidebar__identity"
        variant="ghost"
      >
        <WorkspaceIdentityMark membership={membership} />
      </Button>
      <Dropdown.Popover placement="top">
        <Dropdown.Menu
          onAction={(key) => {
            onSelectWorkspace(String(key));
          }}
        >
          {memberships.map((item) => (
            <Dropdown.Item
              id={item.workspace.id}
              key={item.workspace.id}
              textValue={item.workspace.name}
            >
              <Label>{item.workspace.name}</Label>
              <span className="text-muted text-xs">
                {workspaceRoleLabel(item.role, t)}
              </span>
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown>
  );
}

/** 侧栏：菜单（含可展开子项）、工作区身份。Logo 在顶栏。 */
export function DashboardSidebar({
  mode,
  currentNavigationHref,
  membership,
  membershipReady = true,
  stalePermissions,
  staleWorkspaceStatus,
  staleIsWorkspaceOwner = false,
  memberships = [],
  onSelectWorkspace,
}: DashboardSidebarProps) {
  const isAdmin = mode === "admin";
  const { t } = useAppI18n();

  const menuItems = isAdmin
    ? adminNavItems
    : resolveWorkspaceMenuItems({
        membershipReady,
        permissions: membership?.permissions,
        stalePermissions,
        workspaceStatus: membership?.workspace.status,
        staleWorkspaceStatus,
        isWorkspaceOwner: membership?.role.kind === "owner",
        staleIsWorkspaceOwner,
      });

  return (
    <>
      <Sidebar.Content>
        <Sidebar.Group>
          <Sidebar.Menu
            aria-busy={!isAdmin && !membershipReady}
            aria-label={t(
              isAdmin
                ? "shell.platformNavigationLabel"
                : "shell.mainNavigation",
            )}
            defaultExpandedKeys={expandedNavKeys(
              menuItems,
              currentNavigationHref,
            )}
          >
            <NavigationItems
              currentNavigationHref={currentNavigationHref}
              items={menuItems}
            />
          </Sidebar.Menu>
        </Sidebar.Group>
      </Sidebar.Content>
      <Sidebar.Footer className="application-sidebar__footer">
        {isAdmin ? (
          <div className="application-sidebar__identity">
            <Avatar size="sm">
              <Avatar.Fallback>
                <ShieldCheck width={15} height={15} />
              </Avatar.Fallback>
            </Avatar>
            <div>
              <strong>{t("shell.platformManagement")}</strong>
              <small>{t("shell.platformAdmin")}</small>
            </div>
          </div>
        ) : membership ? (
          <WorkspaceIdentity
            membership={membership}
            memberships={memberships}
            onSelectWorkspace={onSelectWorkspace}
          />
        ) : null}
      </Sidebar.Footer>
    </>
  );
}
